import { useFormikContext } from "formik";
import moment from "moment";
import React from "react";

import { CurrentFinanceYear } from "../../../../config/app-constants";
import { FSAssetsTypesOptions } from "../../../../config/filingConstants";
import { IForeignSharesForm } from "../../../../config/interfaces";
import { UtilService } from "../../../../services";
import {
  DateTimePicker,
  FormField,
  NumericInputField,
  SelectInputField,
  TextInputField
} from "../../../fields";

type Props = {};

const CGForeignSharesForm: React.FC<Props> = (props: Props) => {
  const {} = props;

  const { values, errors, touched, setFieldValue, handleBlur } =
    useFormikContext<IForeignSharesForm>();

  const getNetIncome = (
    TotalSalePrice: string,
    TotalPurchasePrice: string,
    TransferExpense: string
  ) => {
    // (Sale Price - Purchase Price)
    let gainsValue =
      Number(TotalSalePrice || 0) - Number(TotalPurchasePrice || 0) - Number(TransferExpense || 0);
    setFieldValue("Gains", gainsValue);
  };

  return (
    <>
      <section className="mb-5">
        <FormField label="Type of Assets" isRequired={true} sm={4}>
          <SelectInputField
            placeholder="Type of Assets"
            name="TypeAssets"
            isOptionArrayOfObject={false}
            options={FSAssetsTypesOptions}
            value={[values.TypeAssets]}
            onChange={(_selectedAssetsType) => {
              if (_selectedAssetsType) {
                setFieldValue("TypeAssets", _selectedAssetsType.value);
              } else {
                setFieldValue("TypeAssets", undefined);
              }
            }}
            onBlur={handleBlur("TypeAssets")}
            isInvalid={Boolean(errors.TypeAssets) && touched.TypeAssets}
            errorMsg={errors.TypeAssets}
          />
        </FormField>

        <FormField label="Date of Sale" isRequired={true} sm={4}>
          <DateTimePicker
            name="SaleDate"
            placeholder="DD/MM/YYYY"
            value={values.SaleDate ? moment(values.SaleDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("SaleDate", _date);
              } else {
                setFieldValue("SaleDate", "");
              }
            }}
            onBlur={handleBlur("SaleDate")}
            minDate={
              values?.PurchaseDate
                ? moment(values.PurchaseDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            maxDate={moment(CurrentFinanceYear).toDate()}
            isInvalid={Boolean(errors.SaleDate) && touched.SaleDate}
            errorMsg={errors.SaleDate}
            isClearable={true}
          />
        </FormField>

        <FormField label="Date of Purchase" isRequired={true} sm={4}>
          <DateTimePicker
            name="PurchaseDate"
            placeholder="DD/MM/YYYY"
            value={values.PurchaseDate ? moment(values.PurchaseDate).toDate() : ""}
            onChange={(date) => {
              if (date) {
                const _date = UtilService.convertToStandardDateFormat(date);
                setFieldValue("PurchaseDate", _date);
              } else {
                setFieldValue("PurchaseDate", "");
              }
            }}
            onBlur={handleBlur("PurchaseDate")}
            minDate={undefined}
            maxDate={
              values?.SaleDate
                ? moment(values.SaleDate).toDate()
                : moment(CurrentFinanceYear).toDate()
            }
            isInvalid={Boolean(errors.PurchaseDate) && touched.PurchaseDate}
            errorMsg={errors.PurchaseDate}
            isClearable={true}
          />
        </FormField>

        <FormField label="Description of asset sold" isRequired={true} sm={4}>
          <TextInputField
            name="DescriptionAssetSold"
            value={values.DescriptionAssetSold}
            onChange={(value) => {
              setFieldValue("DescriptionAssetSold", value);
            }}
            onBlur={(e) => {
              handleBlur(e);
              setFieldValue("DescriptionAssetSold", values.DescriptionAssetSold.trim());
            }}
            isInvalid={Boolean(errors.DescriptionAssetSold) && touched.DescriptionAssetSold}
            errorMsg={errors.DescriptionAssetSold}
            note="Example: Sale of Shares or Sale of Sold"
          />
        </FormField>

        <FormField label="Total Sale Price" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TotalSalePrice"
            value={values.TotalSalePrice}
            onChange={(value) => {
              setFieldValue("TotalSalePrice", value);
              getNetIncome(value, values.TotalPurchasePrice, values.TransferExpense);
            }}
            onBlur={handleBlur("TotalSalePrice")}
            isInvalid={Boolean(errors.TotalSalePrice) && touched.TotalSalePrice}
            errorMsg={errors.TotalSalePrice}
          />
        </FormField>

        <FormField label="Total Purchase Price" isRequired={true} sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="TotalPurchasePrice"
            value={values.TotalPurchasePrice}
            onChange={(value) => {
              setFieldValue("TotalPurchasePrice", value);
              getNetIncome(values.TotalSalePrice, value, values.TransferExpense);
            }}
            onBlur={handleBlur("TotalPurchasePrice")}
            isInvalid={Boolean(errors.TotalPurchasePrice) && touched.TotalPurchasePrice}
            errorMsg={errors.TotalPurchasePrice}
          />
        </FormField>

        <FormField label="Transfer expenses" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            name="TransferExpense"
            value={values.TransferExpense}
            onBlur={handleBlur("TransferExpense")}
            onChange={(value) => {
              setFieldValue("TransferExpense", value);
              getNetIncome(values.TotalSalePrice, values.TotalPurchasePrice, value);
            }}
            isInvalid={Boolean(errors.TransferExpense) && touched.TransferExpense}
            errorMsg={errors.TransferExpense}
            note="Example: Brokerage fees on sale of shares"
          />
        </FormField>

        <FormField label="Gains" sm={4}>
          <NumericInputField
            showLeftIcon={true}
            placeholder=""
            name="Gains"
            value={values.Gains}
            onChange={(value) => setFieldValue("Gains", value)}
            onBlur={handleBlur("Gains")}
            isInvalid={Boolean(errors.Gains) && touched.Gains}
            errorMsg={errors.Gains}
            isDisabled={true}
            allowNegative={true}
          />
        </FormField>
      </section>
    </>
  );
};

export default CGForeignSharesForm;
